import { useState, useRef } from 'react'
import { PDFDocument } from 'pdf-lib'
import { Upload, Download, X, FileText } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function PDFMerger() {
  const [files, setFiles] = useState([])
  const [mergedPdf, setMergedPdf] = useState(null)
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    const pdfFiles = selectedFiles.filter(file => file.type === 'application/pdf')
    
    if (pdfFiles.length === 0 && selectedFiles.length > 0) {
      alert('Please select PDF files only')
      return
    }

    setFiles(prev => [...prev, ...pdfFiles])
  }

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const reorderFile = (index, direction) => {
    const newFiles = [...files]
    const newIndex = index + direction
    if (newIndex >= 0 && newIndex < files.length) {
      [newFiles[index], newFiles[newIndex]] = [newFiles[newIndex], newFiles[index]]
      setFiles(newFiles)
    }
  }

  const mergePDFs = async () => {
    if (files.length < 2) {
      alert('Please select at least 2 PDF files to merge')
      return
    }

    setProcessing(true)
    try {
      const mergedPdfDoc = await PDFDocument.create()

      for (const file of files) {
        const arrayBuffer = await file.arrayBuffer()
        const pdfDoc = await PDFDocument.load(arrayBuffer)
        const pages = await mergedPdfDoc.copyPages(pdfDoc, pdfDoc.getPageIndices())
        pages.forEach(page => mergedPdfDoc.addPage(page))
      }

      const pdfBytes = await mergedPdfDoc.save()
      const blob = new Blob([pdfBytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      setMergedPdf({ url, blob })
    } catch (error) {
      alert('Error merging PDFs: ' + error.message)
    } finally {
      setProcessing(false)
    }
  }

  const downloadMerged = () => {
    if (!mergedPdf) return
    const link = document.createElement('a')
    link.href = mergedPdf.url
    link.download = 'merged.pdf'
    link.click()
  }

  const clearAll = () => {
    setFiles([])
    setMergedPdf(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Select PDF Files:</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="pdf-upload"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              type="button"
            >
              <Upload className="w-5 h-5 mr-2" />
              Add PDF Files
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Select multiple PDF files to merge. You can reorder them using the arrows.
            </p>
          </div>

          {files.length > 0 && (
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Files to Merge ({files.length}):</h3>
                <Button onClick={clearAll} variant="outline" size="sm">
                  Clear All
                </Button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => reorderFile(index, -1)}
                        disabled={index === 0}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => reorderFile(index, 1)}
                        disabled={index === files.length - 1}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-50"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-red-100 rounded"
                        title="Remove"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={mergePDFs}
            variant="primary"
            className="w-full"
            disabled={files.length < 2 || processing}
          >
            {processing ? 'Merging PDFs...' : 'Merge PDFs'}
          </Button>
        </Card.Body>
      </Card>

      {mergedPdf && (
        <Card>
          <Card.Body>
            <div className="text-center space-y-4">
              <p className="text-green-600 font-semibold">PDFs merged successfully!</p>
              <Button onClick={downloadMerged} variant="primary" size="lg" className="w-full">
                <Download className="w-5 h-5 mr-2" />
                Download Merged PDF
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {files.length === 0 && (
        <Card>
          <Card.Body className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Select PDF files to merge them into one document</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

